import { useEffect, useId, useRef, useState } from 'react';

/**
 * Wave-1 tail — a single, explained taxonomy for the recurring inbox states.
 * The audit flagged that "Needs reply / Snoozed / Muted / Done" appear as pills
 * and row flags with no legend. This ⓘ in the list header opens a compact
 * popover decoding them once (recognition over recall). Read-only.
 */
const ITEMS: ReadonlyArray<{ label: string; desc: string }> = [
  { label: 'Needs reply', desc: 'An inbound message is waiting on your response.' },
  { label: '⏰ Snoozed', desc: 'Hidden until the set time — returns sooner if they reply.' },
  { label: '🔇 Muted', desc: 'Non-actionable: hidden from “Needs reply”, still under “All”.' },
  { label: '✓ Done', desc: 'Cleared from the queue (no reply needed). Reversible.' },
];

export function StatusLegend() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus(); // a11y floor — return focus to the trigger.
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="status-legend">
      <button
        ref={btnRef}
        type="button"
        className="status-legend-btn"
        data-testid="status-legend-button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label="What the status labels mean"
        title="What the status labels mean"
        onClick={() => setOpen((v) => !v)}
      >
        ⓘ
      </button>
      {open ? (
        <section
          id={panelId}
          aria-label="Status legend"
          className="status-legend-panel"
          data-testid="status-legend"
        >
          <p className="status-legend-title">What these mean</p>
          <dl>
            {ITEMS.map((it) => (
              <div key={it.label} className="status-legend-row">
                <dt>{it.label}</dt>
                <dd>{it.desc}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
    </div>
  );
}
